import { createReducer, isAnyOf } from '@reduxjs/toolkit';

import {
  blockAccountSuccess,
  muteAccountSuccess,
} from 'mastodon/actions/accounts';
import {
  followAccountRequest,
  followAccountSuccess,
} from 'mastodon/actions/accounts_typed';
import { blockDomainSuccess } from 'mastodon/actions/domain_blocks';
import {
  fetchSuggestions,
  fetchMostSuggestions,
  dismissSuggestion,
} from 'mastodon/actions/suggestions';
import { createSuggestion } from 'mastodon/models/suggestion';
import type { Suggestion } from 'mastodon/models/suggestion';

interface State {
  items: Suggestion[];
  isLoading: boolean;
  dismissedItemIds: string[];
}

const initialState: State = {
  items: [],
  isLoading: false,
  dismissedItemIds: [],
};

export const suggestionsReducer = createReducer(initialState, (builder) => {
  builder.addCase(fetchSuggestions.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchSuggestions.fulfilled, (state, action) => {
    const newItems = action.payload.map(createSuggestion);
    const validNewItems = newItems.filter(item => !state.dismissedItemIds.includes(item.account_id));
    const existingIds = new Set(state.items.map(item => item.account_id));
    const itemsToAdd = validNewItems.filter(item => !existingIds.has(item.account_id));

    state.items = [...state.items, ...itemsToAdd];
    state.isLoading = false;
  });

  builder.addCase(fetchSuggestions.rejected, (state) => {
    state.isLoading = false;
  });

  builder.addCase(fetchMostSuggestions.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchMostSuggestions.fulfilled, (state, action) => {
    const newItems = action.payload.map(createSuggestion);
    const validNewItems = newItems.filter(item => !state.dismissedItemIds.includes(item.account_id));
    const existingIds = new Set(state.items.map(item => item.account_id));
    const itemsToAdd = validNewItems.filter(item => !existingIds.has(item.account_id));

    state.items = [...state.items, ...itemsToAdd];
    state.isLoading = false;
  });

  builder.addCase(fetchMostSuggestions.rejected, (state) => {
    state.isLoading = false;
  });

  builder.addCase(dismissSuggestion.pending, (state, action) => {
    state.items = state.items.filter(
      (x) => x.account_id !== action.meta.arg.accountId,
    );
    state.dismissedItemIds.push(action.meta.arg.accountId);
  });

  builder.addCase(followAccountRequest, (state, action) => {
    state.items = state.items.filter(
      (x) => x.account_id !== action.payload.id,
    );
  });

  builder.addCase(followAccountSuccess, (state, action) => {
    state.items = state.items.filter(
      (x) => x.account_id !== action.payload.relationship.id,
    );
  });

  builder.addCase(blockDomainSuccess, (state, action) => {
    state.items = state.items.filter(
      (x) =>
        !action.payload.accounts.some((account) => account.id === x.account_id),
    );
  });

  builder.addMatcher(
    isAnyOf(blockAccountSuccess, muteAccountSuccess),
    (state, action) => {
      state.items = state.items.filter(
        (x) => x.account_id !== action.payload.relationship.id,
      );
    },
  );
});
